import React, { useEffect, useState } from "react";
import {
  Box,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRightOutlined,
  ExpandLess,
  ExpandMore,
  HomeOutlined,
  Inventory2Outlined,
  CoPresentOutlined,
  SafetyDividerOutlined,
  MonetizationOnOutlined,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import FlexBetween from "./FlexBetween";
import logoImage from "./../assets/logo-prima.png";

// Nav item structure
const navItems = [
  {
    text: "Dashboard",
    path: "dashboard",
    icon: <HomeOutlined />,
  },
  {
    text: "Data Master",
    icon: <SafetyDividerOutlined />,
    children: [
      { text: "Departemen", path: "departemen" },
      { text: "Divisi", path: "divisi" },
      { text: "Jabatan", path: "jabatan" },
      // { text: "Karyawan", path: "karyawan" },
      { text: "Karyawan", path: "karyawanbaru" },
      { text: "Notifikasi", path: "notifikasi" },
      { text: "Panduan", path: "panduan" },
      { text: "Aspirasi", path: "aspirasi" },
      { text: "Peringatan", path: "peringatan" },
      { text: "Jenjang", path: "jenjang" },
    ],
  },
  {
    text: "Stockopname",
    icon: <Inventory2Outlined />,
    children: [
      { text: "Data Stockopname", path: "stockopname" },
      { text: "Peserta Stockopname", path: "pesertaso" },
    ],
  },
  {
    text: "Absensi",
    icon: <CoPresentOutlined />,
    children: [
      { text: "Absensi Hari Ini", path: "absensihariini" },
      { text: "Rekap Absen Harian", path: "rekapabsensiharian" },
      { text: "Rekap Absen Bulanan", path: "rekapabsensibulanan" },
    ],
  },
  {
    text: "Keuangan",
    icon: <MonetizationOnOutlined />,
    children: [
      { text: "Tabungan", path: "tabungan" },
      { text: "Rekap Tabungan", path: "rekaptabungan" },
      { text: "Gaji Karyawan", path: "penggajian" },
      { text: "Setoran Toko", path: "setoran" },
      { text: "Kas Besar", path: "kasbesar" },
    ],
  },
];

const Sidebar = ({
  drawerWidth,
  isSidebarOpen,
  setIsSidebarOpen,
  isNonMobile,
}) => {
  const { pathname } = useLocation();
  const [active, setActive] = useState("");
  const [openSubmenus, setOpenSubmenus] = useState({});
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    setActive(pathname.substring(1));
  }, [pathname]);

  const toggleSubmenu = (text) => {
    setOpenSubmenus((prev) => ({
      ...prev,
      [text]: !prev[text],
    }));
  };

  return (
    <Box component="nav">
      {isSidebarOpen && (
        <Drawer
          open={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          variant="persistent"
          anchor="left"
          sx={{
            width: drawerWidth,
            "& .MuiDrawer-paper": {
              color: theme.palette.secondary[200],
              backgroundColor: theme.palette.background.alt,
              borderWidth: isNonMobile ? 0 : "2px",
              width: drawerWidth,
            },
          }}
        >
          <Box width="100%">
            <Box m="1.5rem 2rem 2rem 3rem">
              <FlexBetween color={theme.palette.secondary.main}>
                <Box display="flex" alignItems="center" gap="0.5rem">
                  <img
                    src={logoImage}
                    alt="Logo"
                    style={{ width: "120px", height: "auto" }}
                  />
                </Box>
                {!isNonMobile && (
                  <IconButton onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
                    <ChevronLeft />
                  </IconButton>
                )}
              </FlexBetween>
            </Box>

            <List>
              {navItems.map((item, index) => {
                const isItemActive = active === item.path;

                if (item.children) {
                  const isOpen = openSubmenus[item.text] || false;
                  return (
                    <Box key={index}>
                      <ListItem disablePadding>
                        <ListItemButton
                          onClick={() => toggleSubmenu(item.text)}
                        >
                          <ListItemIcon
                            sx={{
                              ml: "2rem",
                              color: theme.palette.secondary[200],
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText primary={item.text} />
                          {isOpen ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                      </ListItem>
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                          {item.children.map((child) => {
                            const isChildActive = active === child.path;
                            return (
                              <ListItem key={child.path} disablePadding>
                                <ListItemButton
                                  sx={{
                                    pl: 6,
                                    backgroundColor: isChildActive
                                      ? theme.palette.secondary[900]
                                      : "transparent",
                                    color: isChildActive
                                      ? theme.palette.primary[600]
                                      : theme.palette.secondary[100],
                                  }}
                                  onClick={() => {
                                    navigate(`/${child.path}`);
                                    setActive(child.path);
                                  }}
                                >
                                  <ListItemIcon
                                    sx={{
                                      color: isChildActive
                                        ? theme.palette.primary[600]
                                        : theme.palette.secondary[200],
                                    }}
                                  >
                                    {child.icon || <ChevronRightOutlined />}
                                  </ListItemIcon>
                                  <ListItemText primary={child.text} />
                                </ListItemButton>
                              </ListItem>
                            );
                          })}
                        </List>
                      </Collapse>
                    </Box>
                  );
                }

                // Single menu item
                return (
                  <ListItem key={item.path} disablePadding>
                    <ListItemButton
                      onClick={() => {
                        navigate(`/${item.path}`);
                        setActive(item.path);
                      }}
                      sx={{
                        backgroundColor: isItemActive
                          ? theme.palette.secondary[900]
                          : "transparent",
                        color: isItemActive
                          ? theme.palette.primary[600]
                          : theme.palette.secondary[100],
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          ml: "2rem",
                          color: isItemActive
                            ? theme.palette.primary[600]
                            : theme.palette.secondary[200],
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText primary={item.text} />
                      {isItemActive && (
                        <ChevronRightOutlined sx={{ ml: "auto" }} />
                      )}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Drawer>
      )}
    </Box>
  );
};

export default Sidebar;
